import React, { useCallback, useEffect, useMemo, useState, } from 'react';

import { useTranslation, } from 'react-i18next';
import { useNavigate, } from 'react-router-dom';
import {
  PhonenumberMonthlyCostParentAddForm,
  PhonenumberMonthlyCostParentAddInitialValues,
  PhonenumberMonthlyCostParentComponents,
  PhonenumberMonthlyCostParentAdditionalParams,
  PhonenumberMonthlyCostParentFormOptions,
  onPhonenumberMonthlyCostParentFormSaved,
  IPhonenumberMonthlyCostParentFormOptionsProps,
  AlertError,
  queryParam,
} from '@Utils';
import { AtlasForm, IFormOptions, } from 'atlas-form';
import { CrudEnum, ISharedCreatePhonenumberMonthlyCostParent, PermissionEnum, } from 'atlas-shared';
import { createPhonenumberMonthlyCostParent, fetchPhonenumberMonthlyCostParent, getPhonenumberMonthlyCostParentCreateSchema, } from '@Api';
import { RuleProtected, } from '@Containers';
import { withAdd, IWithAddProps, } from '@Hocs/with.add';
import { useAbilities, useAuth, useOrganizations, } from '@Hooks';
import { AppSpinner, } from '@Components';
import { useAppDispatch, } from '@Store';

interface IProps extends IWithAddProps<ISharedCreatePhonenumberMonthlyCostParent> {}

const PhonenumberMonthlyCostParentFormAdd = ({ values, setValues, save, formSchema, schemaLoading, saving, initialValues, title, onSaved, onCancel, onChange, hasAdvanced = false, }: IProps) => {
  const organizations = useOrganizations();
  const abilities = useAbilities();
  const { t, } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();
  const dispatch = useAppDispatch();

  const [cloned, setCloned, ] = useState<Partial<ISharedCreatePhonenumberMonthlyCostParent>>();

  const param_props: IPhonenumberMonthlyCostParentFormOptionsProps = {
    t,
    is_new: true,
    abilities,
    organizations: organizations.organizations,
  };
  const formLayout = useMemo(() => PhonenumberMonthlyCostParentAddForm(values, param_props, t), [values, organizations, t, abilities, ]);
  const options: IFormOptions = useMemo(() => PhonenumberMonthlyCostParentFormOptions(values || PhonenumberMonthlyCostParentAddInitialValues, param_props, t), [values, organizations, t, abilities, ]);
  
  const onValuesChange = useCallback((changedValue: any, values: ISharedCreatePhonenumberMonthlyCostParent, form) => {
    setValues(values);
    onChange?.(values, changedValue, form);
  }, [setValues, ]);
  const onFinish = useCallback((values: ISharedCreatePhonenumberMonthlyCostParent) => save(() => createPhonenumberMonthlyCostParent(values).then(
    res => onSaved ? onSaved(res, navigate) : onPhonenumberMonthlyCostParentFormSaved(t, 'add', res, navigate),
    err => {
      AlertError(t, {
        content: err,
      });
    }
  )), [save, navigate, t, ]);
  
  useEffect(() => {
    const id = queryParam('clone');

    if (id)
      fetchPhonenumberMonthlyCostParent(+id)
        .then(({
          title,
          currency,
          phonenumber_monthly_cost_list,
        }) => {
          setCloned({
            title,
            currency,
            phonenumber_monthly_cost_list,
          });
        });
    else
      setCloned({});
  }, []);
  
  if (schemaLoading || !cloned)
    return <AppSpinner />;

  if (!formSchema)
    return <>{t('SCHEMA_NOT_FOUND')}</>;
  
  if (!organizations.loaded)
    return <>{t('LOADING_DEPENDENCIES')}</>;

  return (
    <RuleProtected
      isAllowed={() => !values || abilities.can(null, CrudEnum.Insert, PermissionEnum.PhonenumberMonthlyCostParent, values)}
    >
      <AtlasForm
        dispatch={dispatch}
        formLayout={formLayout}
        form={formSchema}
        options={options}
        components={PhonenumberMonthlyCostParentComponents}
        additionalParams={PhonenumberMonthlyCostParentAdditionalParams(auth, param_props)}
        onFinish={onFinish}
        onCancel={onCancel}
        onValuesChange={onValuesChange}
        initialValues={{ ...(cloned || {}), ...PhonenumberMonthlyCostParentAddInitialValues, ...initialValues, }}
        saving={saving}
        title={title ? t(title) : `${t('ADD')} ${t('PHONENUMBER_MONTHLY_COST_PARENT')}`}
        hasAdvanced={hasAdvanced}
      />
    </RuleProtected>
  );
};

export const PhonenumberMonthlyCostParentFormAddView = React.memo(withAdd(PhonenumberMonthlyCostParentFormAdd, getPhonenumberMonthlyCostParentCreateSchema, PhonenumberMonthlyCostParentAddInitialValues));
